import { isUndefined } from 'lodash';
import { useEffect, useState } from 'react';
import { useCookies } from 'react-cookie';
import { useSelector } from 'react-redux';
import {
  trackCloseGDPRMessage,
  trackNavigationItemClick,
  trackShowGDPRMessagePopup,
} from '../../common/analytics/analyticsTracker';
import * as eventActions from '../../common/analytics/events/constants/eventActions';
import { generateEventSourceByPageType } from '../../common/analytics/helpers/analyticsSourceHelper';
import CookieCloseIcon from '../../common/assets/icons/cookie-close-3x.png';
import CookieLockIcon from '../../common/assets/icons/cookie-lock-3x.png';
import config from '../../common/config';
import { ISO3166StandardEUList } from '../../common/helpers/constants';
import { LandingPageTypes } from '../../common/helpers/domain/landingPages';
import { formatResource } from '../../common/helpers/localization/contentResourceHelper';
import logger from '../../common/helpers/logger/logger';
import commonResource from '../../locales/th/common.json';
import { WholeState } from '../../store/common/type';
import DesktopSearch from '../search/DesktopSearch';
import MobileSearch from '../search/MobileSearch';
import DesktopNavigation from './component/desktopNavigation/DesktopNavigation';
import HeaderLogo from './component/headerLogo';
import { MobileNavigation } from './component/mobileNavigation/MobileNavigation';
import { getHeaderMenu } from './component/navBar/headerMenuUtil';
import {
  CookieCloseText,
  CookieText,
  DesktopBottomWrapper,
  HeaderCookieContainer,
  HeaderCookieWrapper,
  ImageClose,
  ImageLock,
  InnerWrapper,
  Wrapper,
} from './Header.style';

const OneYearInSeconds = 365 * 24 * 60 * 60;

interface HeaderProps {
  source: string;
}

function Header({ source }: HeaderProps) {
  const headerMenu = getHeaderMenu();
  const [isEuUser, setIsEuUser] = useState(false);
  const [hasCookiePopup, setCookiePopup] = useState(false);
  const [cookies, setCookie] = useCookies<string>(['gdpr_notification']);
  const userInfo = useSelector((state: WholeState) =>
    state.common !== undefined ? state.common.user : {},
  );

  useEffect(() => {
    if (cookies.gdpr_notification !== 'yes') {
      setCookiePopup(true);
    }
  }, [cookies.gdpr_notification]);

  const cookieTemplate = () => {
    return (
      <HeaderCookieWrapper className="sitenote-wrapper">
        <HeaderCookieContainer className="sitenote-container">
          <div className="icon">
            <ImageLock src={CookieLockIcon} alt={'lock-icon'} />
          </div>
          <div>
            <CookieText
              className="sitenote-placeholder"
              dangerouslySetInnerHTML={{
                __html: formatResource(commonResource.cookieMessage, {
                  policyPageLink:
                    '<a style="color:#999;text-decoration:underline;" href="' +
                    config.privacyUrl +
                    '" target="_blank">' +
                    commonResource.cookieLinkText +
                    '</a>',
                }),
              }}
            />
            <CookieCloseText
              className="sitenote-ok-button"
              onClick={() => {
                trackCloseGDPRMessage(
                  userInfo,
                  eventActions.CLOSE_GDPR_POPUP_MESSAGE_BY_OK,
                  source,
                );
                setCookiePolicy();
              }}
            >
              {commonResource.cookieAgreeButton}
            </CookieCloseText>
          </div>
          <div className="sitenote-close-button">
            <ImageClose
              onClick={() => {
                trackCloseGDPRMessage(
                  userInfo,
                  eventActions.CLOSE_GDPR_POPUP_MESSAGE_BY_X,
                  source,
                );
                setCookiePolicy();
              }}
              src={CookieCloseIcon}
              alt={'close-icon'}
            />
          </div>
        </HeaderCookieContainer>
      </HeaderCookieWrapper>
    );
  };

  const setCookiePolicy = () => {
    setCookie('gdpr_notification', 'yes', {
      sameSite: 'lax',
      secure: true,
      path: '/',
      maxAge: OneYearInSeconds,
    });
    setCookiePopup(false);
  };

  useEffect(() => {
    const isEuMember = (code: string) => {
      return ISO3166StandardEUList.indexOf(code.toUpperCase()) !== -1;
    };

    // Get cloudfront viewer country cookie value and set boolean
    const cloudFrontViewerCountryCookie =
      cookies['x-cloudfront-viewer-country'];

    // Get cloudflare viewer country cookie value and set boolean
    const cloudFlareViewerCountryCookie =
      cookies['x-cloudflare-viewer-country'];

    const viewerCountryCookie = cloudFrontViewerCountryCookie
      ? cloudFrontViewerCountryCookie
      : cloudFlareViewerCountryCookie;

    if (!isUndefined(viewerCountryCookie)) {
      const euMember = isEuMember(viewerCountryCookie);
      if (euMember && hasCookiePopup) {
        trackShowGDPRMessagePopup(
          userInfo,
          eventActions.SHOW_GDPR_POPUP_MESSAGE,
          source,
        );
      }
      setIsEuUser(isEuMember(viewerCountryCookie));
    }
  }, [setIsEuUser, userInfo, cookies, source, hasCookiePopup]);

  const onClickNavigationItem = (itemKey: string) => {
    const targetSource = generateEventSourceByPageType(
      itemKey as LandingPageTypes,
    );
    if (targetSource && source) {
      trackNavigationItemClick(userInfo, source, targetSource);
    } else {
      logger.logWarning(
        'Header',
        'Failed to generate navigation click GA event',
        {
          itemKey,
          source,
          targetSource,
        },
      );
    }
  };

  return (
    <Wrapper>
      {isEuUser && hasCookiePopup && cookieTemplate()}
      <InnerWrapper>
        <MobileNavigation
          navbarItems={headerMenu}
          onClickNavigationItem={onClickNavigationItem}
        />
        <HeaderLogo />
        <DesktopBottomWrapper>
          <div className="bottom-left">
            <DesktopNavigation
              navBarItems={headerMenu}
              onClickNavigationItem={onClickNavigationItem}
            />
          </div>
          <div className="bottom-right">
            <DesktopSearch />
            {/* Hide account options temporarily until the login issue is resolved */}
            {/* <DesktopAccount /> */}
          </div>
        </DesktopBottomWrapper>
        <MobileSearch />
      </InnerWrapper>
    </Wrapper>
  );
}

export default Header;
